import { useEffect, useRef } from 'react';
import { useReducedMotion } from '@/lib/motion';

/**
 * Ambient "market universe" — a field of ~460 faint points (the S&P 500 the
 * engine reads each day). Most drift quietly; a handful pulse cyan, as if being
 * evaluated. Pure canvas so 460 points stay cheap. Colours are read from the
 * active theme tokens (never hardcoded) so it re-skins with the palette.
 *
 * Respects reduced motion: renders one static frame with no drift or pulsing.
 */
export function UniverseField({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const styles = getComputedStyle(document.documentElement);
    const ink = styles.getPropertyValue('--nq-slate-400').trim() || '#8b97a8';
    const signal = styles.getPropertyValue('--nq-blue').trim() || '#25b4e0';

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Pointer position in canvas-local coords (-1 when absent).
    const mouse = { x: -1, y: -1 };

    type Dot = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
      lit: boolean;
      phase: number;
    };
    let dots: Dot[] = [];

    const COUNT = 460;

    function seed() {
      dots = Array.from({ length: COUNT }, (_, i) => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.12,
        vy: (Math.random() - 0.5) * 0.12,
        r: Math.random() * 1.3 + 0.5,
        lit: i % 11 === 0,
        phase: Math.random() * Math.PI * 2,
      }));
    }

    function resize() {
      const rect = canvas!.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas!.width = Math.floor(width * dpr);
      canvas!.height = Math.floor(height * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    }

    let raf = 0;
    let t = 0;

    function draw() {
      ctx!.clearRect(0, 0, width, height);
      const R = 130; // cursor influence radius
      for (const d of dots) {
        if (!reduced) {
          d.x += d.vx;
          d.y += d.vy;
          if (d.x < 0) d.x = width;
          if (d.x > width) d.x = 0;
          if (d.y < 0) d.y = height;
          if (d.y > height) d.y = 0;
        }
        // Proximity to the pointer → brighten + ignite in cyan.
        let near = 0;
        if (mouse.x >= 0) {
          const dist = Math.hypot(d.x - mouse.x, d.y - mouse.y);
          if (dist < R) near = 1 - dist / R;
        }
        const pulse = d.lit && !reduced ? 0.5 + 0.5 * Math.sin(t * 0.05 + d.phase) : 1;
        const litNow = d.lit || near > 0.15;
        ctx!.beginPath();
        ctx!.arc(d.x, d.y, (litNow ? d.r + 0.4 : d.r) + near * 1.2, 0, Math.PI * 2);
        ctx!.fillStyle = litNow ? signal : ink;
        const base = d.lit ? 0.25 + 0.55 * pulse : 0.22;
        ctx!.globalAlpha = Math.min(1, base + near * 0.6);
        ctx!.fill();
      }
      ctx!.globalAlpha = 1;
      if (!reduced) {
        t += 1;
        raf = requestAnimationFrame(draw);
      }
    }

    function onPointerMove(e: PointerEvent) {
      const rect = canvas!.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      mouse.x = x >= 0 && x <= width && y >= 0 && y <= height ? x : -1;
      mouse.y = y;
    }
    function onPointerLeave() {
      mouse.x = -1;
    }

    resize();
    draw();
    window.addEventListener('resize', resize);
    if (!reduced) {
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerleave', onPointerLeave);
    }
    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={className}
    />
  );
}
